/**
 * Create Matrix page - form to generate a new permitting matrix
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { US_STATES } from '../data/us-states';
import { getCountiesForState } from '../data/us-counties';

interface PowerSource {
  type: string;
  mw: string;
  generatorType?: string; // For natural gas sources
}

const LOADING_MESSAGES = [
  'Researching state regulations...',
  'Exploring county requirements...',
  'Analyzing federal permits...',
  'Reviewing local ordinances...',
  'Cross-referencing permit databases...',
  'Calculating timelines...',
  'Estimating costs...',
  'Compiling requirements...',
  'Validating thresholds...',
  'Building your matrix...',
];

export const CreateMatrix: React.FC = () => {
  const [formData, setFormData] = useState({
    project_name: '',
    project_type: 'data_center',
    state: '',
    county: '',
    city: '',
    land_area_acres: '',
    power_capacity_mw: '',
    building_size_sqft: '',
    cooling_type: '',
    natural_gas_generator_type: '', // For gas projects
  });
  const [powerSources, setPowerSources] = useState<PowerSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const countiesForState = getCountiesForState(formData.state);

  // Rotate loading messages every 3 seconds
  React.useEffect(() => {
    if (loading) {
      const messageInterval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // For data centers, require power source allocation
    if (formData.project_type === 'data_center' && powerSources.length === 0) {
      setError('Power source allocation is required for data center projects. Please add at least one power source.');
      return;
    }

    // Validate power sources sum to total capacity if any sources are added
    if (powerSources.length > 0) {
      const totalAllocated = powerSources.reduce((sum, source) => {
        return sum + (parseFloat(source.mw) || 0);
      }, 0);
      const totalCapacity = parseFloat(formData.power_capacity_mw);

      if (Math.abs(totalAllocated - totalCapacity) > 0.01) {
        setError(
          `Power source allocation (${totalAllocated.toFixed(1)} MW) must equal total capacity (${totalCapacity.toFixed(1)} MW)`
        );
        return;
      }
    }

    setLoading(true);
    setLoadingMessageIndex(0);

    try {
      const payload: any = {
        project_name: formData.project_name,
        state: formData.state,
        county: formData.county,
        city: formData.city || undefined,
        power_capacity_mw: parseFloat(formData.power_capacity_mw),
        land_area_acres: parseFloat(formData.land_area_acres),
        project_type: formData.project_type,
        has_integrated_power: powerSources.length > 0,
      };

      // Add power sources as structured array (required for data centers)
      if (powerSources.length > 0) {
        payload.power_sources = powerSources.map(s => ({
          source_type: s.type,
          capacity_mw: parseFloat(s.mw)
        }));
      }

      // Add optional fields if provided
      if (formData.building_size_sqft) {
        payload.building_size_sqft = parseFloat(formData.building_size_sqft);
      }
      if (formData.cooling_type) {
        payload.cooling_type = formData.cooling_type;
      }

      await apiClient.generateMatrix(payload);

      // Navigate to dashboard immediately - user can see pending status there
      // Matrix detail page will handle polling
      navigate('/dashboard');

    } catch (err: any) {
      setLoading(false);
      if (err.response?.status === 402) {
        setError('You need an active subscription to generate more matrices. Please upgrade your plan.');
      } else if (err.response?.status === 400) {
        setError('Invalid request. Please check your inputs and try again.');
      } else if (err.response?.status === 500) {
        setError('Server error. Our team has been notified. Please try again in a few moments.');
      } else if (err.message?.includes('network') || err.message?.includes('timeout')) {
        setError('Connection issue. Please check your internet connection and try again.');
      } else {
        setError('Unable to start matrix generation. Please try again or contact support if the problem persists.');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'state') {
        return {
          ...prev,
          state: value,
          county: '',
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const addPowerSource = () => {
    setPowerSources([...powerSources, { type: 'grid', mw: '' }]);
  };

  const removePowerSource = (index: number) => {
    setPowerSources(powerSources.filter((_, i) => i !== index));
  };

  const updatePowerSource = (index: number, field: 'type' | 'mw' | 'generatorType', value: string) => {
    const updated = [...powerSources];
    if (field === 'type') {
      updated[index].type = value;
      // Reset generator type if switching away from natural gas
      if (value !== 'natural_gas') {
        delete updated[index].generatorType;
      }
    } else if (field === 'generatorType') {
      updated[index].generatorType = value;
    } else {
      updated[index][field] = value;
    }
    setPowerSources(updated);
  };

  const getTotalAllocated = () => {
    return powerSources.reduce((sum, source) => sum + (parseFloat(source.mw) || 0), 0);
  };

  const getRemainingCapacity = () => {
    const total = parseFloat(formData.power_capacity_mw) || 0;
    const allocated = getTotalAllocated();
    return total - allocated;
  };

  const isFormValid = () => {
    // Check all required fields are filled
    if (!formData.project_name || !formData.state || !formData.county ||
        !formData.land_area_acres || !formData.power_capacity_mw) {
      return false;
    }

    // For data centers, check power source allocation
    if (formData.project_type === 'data_center') {
      if (powerSources.length === 0) return false;
      // Use same tolerance as submission validation (0.01 MW)
      if (Math.abs(getRemainingCapacity()) > 0.01) return false;
      // Check that all natural gas sources have generator type selected
      if (powerSources.some(source => source.type === 'natural_gas' && !source.generatorType)) {
        return false;
      }
    }

    return true;
  };

  // Show loading state immediately when generating
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
          <img src="/logo-primary.svg" alt="Generating" className="h-16 w-16 ring-pulse" />
          <div className="text-center">
            <h2 className="text-xl md:text-2xl font-bold mb-2">Generating Your Matrix</h2>
            <p className="text-text-secondary text-sm md:text-base font-mono">
              {LOADING_MESSAGES[loadingMessageIndex]}
            </p>
          </div>
          <div className="text-xs text-text-muted font-mono">
            This usually takes 20-30 seconds
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="mb-8 md:mb-12">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Generate Matrix</h1>
        <p className="text-text-secondary text-sm md:text-base">
          Input project parameters for regulatory analysis.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Project Type - First Row */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-mono text-text-muted mb-2">
              PROJECT TYPE *
            </label>
            <select
              name="project_type"
              value={formData.project_type}
              onChange={handleChange}
              required
              className="input"
            >
              <option value="data_center">Data Center</option>
              <option value="wind">Wind</option>
              <option value="solar">Solar</option>
              <option value="battery">Battery Storage (BESS)</option>
              <option value="gas">Gas</option>
            </select>
          </div>

          {/* Project Name */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-mono text-text-muted mb-2">
              PROJECT NAME *
            </label>
            <input
              type="text"
              name="project_name"
              value={formData.project_name}
              onChange={handleChange}
              required
              placeholder="Phoenix Data Center Phase 1"
              className="input"
            />
          </div>

          {/* Country - Fixed to USA */}
          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-mono text-text-muted mb-2">
              COUNTRY
            </label>
            <div className="relative">
              <input
                type="text"
                value="United States"
                disabled
                className="input bg-bg-surface cursor-not-allowed opacity-75"
              />
              <p className="text-xs text-text-muted mt-1 italic">
                Additional geographies will be supported soon
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono text-text-muted mb-2">
              STATE *
            </label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              className="input"
            >
              <option value="">Select state...</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-mono text-text-muted mb-2">
              COUNTY *
            </label>
            <select
              name="county"
              value={formData.county}
              onChange={handleChange}
              required
              disabled={!formData.state}
              className="input"
            >
              <option value="">
                {formData.state ? 'Select county...' : 'Select a state first'}
              </option>
              {countiesForState.map((county) => (
                <option key={county} value={county}>
                  {county}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-mono text-text-muted mb-2">
              CITY
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Phoenix"
              className="input"
            />
          </div>

          <div>
            <label className="block text-xs font-mono text-text-muted mb-2">
              LAND AREA (acres) *
            </label>
            <input
              type="number"
              name="land_area_acres"
              value={formData.land_area_acres}
              onChange={handleChange}
              required
              min="0"
              step="0.1"
              placeholder="10"
              className="input font-mono"
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-mono text-text-muted mb-2">
              TOTAL CAPACITY (MW) *
            </label>
            <input
              type="number"
              name="power_capacity_mw"
              value={formData.power_capacity_mw}
              onChange={handleChange}
              required
              min="0"
              step="0.1"
              placeholder="50"
              className="input font-mono"
            />
          </div>

          {/* Generator Type for Gas Projects */}
          {formData.project_type === 'gas' && (
            <div className="col-span-1 md:col-span-2">
              <label className="block text-xs font-mono text-text-muted mb-2">
                GENERATOR TYPE
              </label>
              <select
                name="natural_gas_generator_type"
                value={formData.natural_gas_generator_type}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select type...</option>
                <option value="fuel_cells">Fuel Cells</option>
                <option value="recips">Reciprocating Engines</option>
                <option value="turbines">Turbines</option>
              </select>
            </div>
          )}

          {/* Power Source Allocation - Required for Data Centers */}
          {formData.project_type === 'data_center' && (
            <div className="col-span-1 md:col-span-2 mt-2 space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-mono text-text-muted">
                  POWER SOURCE ALLOCATION *
                </label>
                <button
                  type="button"
                  onClick={addPowerSource}
                  className="text-xs font-mono text-accent-primary hover:text-accent-secondary"
                >
                  + ADD SOURCE
                </button>
              </div>

              {powerSources.map((source, index) => (
                <div key={index} className="space-y-3">
                  <div className="flex flex-col md:grid md:grid-cols-[1fr_1fr_auto] gap-3">
                    <select
                      value={source.type}
                      onChange={(e) => updatePowerSource(index, 'type', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="grid">Grid</option>
                      <option value="utility_power">Utility Power</option>
                      <option value="natural_gas">Natural Gas</option>
                      <option value="diesel">Diesel</option>
                      <option value="solar">Solar</option>
                      <option value="wind">Wind</option>
                      <option value="battery">Battery</option>
                      <option value="coal">Coal</option>
                      <option value="nuclear">Nuclear</option>
                      <option value="hydro">Hydro</option>
                      <option value="fuel_cell">Fuel Cell</option>
                    </select>
                    <div className="flex gap-3">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          value={source.mw}
                          onChange={(e) => updatePowerSource(index, 'mw', e.target.value)}
                          placeholder="0"
                          min="0"
                          step="0.1"
                          className="input font-mono text-sm pr-10 w-full"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono">
                          MW
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePowerSource(index)}
                        className="px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-950/30 rounded border border-border-dim md:hidden"
                      >
                        ×
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePowerSource(index)}
                      className="hidden md:block px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-950/30 rounded border border-border-dim"
                    >
                      ×
                    </button>
                  </div>

                  {/* Generator Type for Natural Gas */}
                  {source.type === 'natural_gas' && (
                    <div className="pl-3 border-l-2 border-border-dim">
                      <label className="block text-xs font-mono text-text-muted mb-2">
                        GENERATOR TYPE <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={source.generatorType || ''}
                        onChange={(e) => updatePowerSource(index, 'generatorType', e.target.value)}
                        className="input text-sm"
                        required
                      >
                        <option value="">Select type...</option>
                        <option value="fuel_cells">Fuel Cells</option>
                        <option value="recips">Reciprocating Engines</option>
                        <option value="turbines">Turbines</option>
                      </select>
                    </div>
                  )}
                </div>
              ))}

              {/* Always show notification until MW matches */}
              {(powerSources.length === 0 || Math.abs(getRemainingCapacity()) > 0.01) && (
                <div className="p-4 bg-amber-950/20 border border-amber-900/50 rounded text-amber-400 text-sm">
                  <p className="font-medium mb-1">⚠️ Power source allocation required</p>
                  <p className="text-xs text-amber-300/80">
                    {powerSources.length === 0 ? (
                      <>
                        Click "+ ADD SOURCE" to specify how your data center will be powered.
                        The total MW across all sources must equal{' '}
                        <span className="font-bold text-amber-400">
                          {formData.power_capacity_mw ? `${parseFloat(formData.power_capacity_mw).toFixed(1)} MW` : '___'}
                        </span>.
                      </>
                    ) : (
                      <>
                        Power allocation must exactly equal total capacity:{' '}
                        <span className="font-bold text-amber-400">
                          {formData.power_capacity_mw ? `${parseFloat(formData.power_capacity_mw).toFixed(1)} MW` : '___'}
                        </span>
                        {'. '}
                        Currently allocated: <span className="font-bold">{getTotalAllocated().toFixed(1)} MW</span>
                        {formData.power_capacity_mw && (
                          <>
                            {' '}(
                            {getRemainingCapacity() > 0
                              ? `${getRemainingCapacity().toFixed(1)} MW remaining`
                              : `${Math.abs(getRemainingCapacity()).toFixed(1)} MW over`}
                            ).
                          </>
                        )}
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Success indicator when MW matches */}
              {powerSources.length > 0 && Math.abs(getRemainingCapacity()) <= 0.01 && (
                <div className="p-3 bg-green-950/20 border border-green-900/50 rounded text-green-400 text-xs flex items-center gap-2">
                  <span>✓</span>
                  <span>Power allocation complete: {getTotalAllocated().toFixed(1)} MW across {powerSources.length} source{powerSources.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          )}

          {/* Additional Project Details Section - Only for Data Centers */}
          {formData.project_type === 'data_center' && (
            <>
              <div className="col-span-1 md:col-span-2 mt-6 pt-6 border-t border-border-dim">
                <h3 className="text-sm font-mono text-text-muted mb-4">ADDITIONAL DETAILS (OPTIONAL)</h3>
              </div>

              <div>
                <label className="block text-xs font-mono text-text-muted mb-2">
                  BUILDING SIZE (sqft)
                </label>
                <input
                  type="number"
                  name="building_size_sqft"
                  value={formData.building_size_sqft}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  placeholder="100000"
                  className="input font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-mono text-text-muted mb-2">
                  COOLING TYPE
                </label>
                <select
                  name="cooling_type"
                  value={formData.cooling_type}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Select type...</option>
                  <option value="air">Air</option>
                  <option value="water">Water</option>
                  <option value="closed_loop">Closed Loop</option>
                </select>
              </div>
            </>
          )}
        </div>

        {error && (
          <div className="mt-6 p-4 bg-red-950/30 border border-red-900/50 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-8">
          <button
            type="submit"
            disabled={loading || !isFormValid()}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <img src="/logo-primary.svg" alt="" className="h-4 w-4 ring-pulse inline" />
                Generating Matrix...
              </span>
            ) : (
              'Generate Matrix'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
